import {Component, EventEmitter, Input, Output, OnDestroy} from '@angular/core';
import {MatButtonModule} from '@angular/material/button';
import {CommonModule} from '@angular/common';
import {MatIconModule} from '@angular/material/icon';
import {MatMenuModule} from '@angular/material/menu';
import {MatToolbarModule} from '@angular/material/toolbar';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatTooltipModule} from '@angular/material/tooltip';
import {MatTabsModule} from '@angular/material/tabs';
import {ReactiveFormsModule} from '@angular/forms';
import {ChatService} from '../../../core/services/chat.service';
import {GenericoService} from '../../../core/services/generico.service';
import {first, firstValueFrom, Subject, takeUntil} from 'rxjs';
import {getUserId} from '../../../core/helpers/utilities';
import {MatDivider} from '@angular/material/divider';

@Component({
  selector: 'app-admin-item-chat',
  standalone: true,
  imports: [
    CommonModule,
    MatToolbarModule,
    MatIconModule,
    MatFormFieldModule,
    MatTooltipModule,
    MatTabsModule,
    MatMenuModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatDivider,
  ],
  templateUrl: './admin-item-chat.component.html',
  styleUrl: './admin-item-chat.component.scss'
})
export class AdminItemChatComponent implements OnDestroy {
  private destroyed$ = new Subject<void>();
  private destroyed = false;
  ngOnDestroy(): void { this.destroyed = true; this.destroyed$.next(); this.destroyed$.complete(); }
  /** Indica si el agente actual es dueño del chat ('own') o no */
  @Input() agente: any;

  /** Objeto que contiene la información del chat */
  @Input() chat: any;

  /** Emite evento al seleccionar el chat (no usado actualmente, pero preparado) */
  @Output() selectedChat = new EventEmitter();

  constructor(
    private chatService: ChatService,
    private genericoService: GenericoService
  ) {
  }

  /**
   * Acción para abrir el chat. Si el agente no es el dueño y no está en modo lectura,
   * solicita confirmación para tomar el chat.
   * @param read - true si se desea abrir el chat en modo solo lectura
   */
  async newChatView(read: boolean) {
    if (this.agente === 'own') {
      this.abrirChat(read);
      return;
    }

    if (read) {

      const isConfirmed = await this.genericoService.confirmDialog(
        this.generarTextoConfirmacion()
      );
      if (!isConfirmed || this.destroyed) {
        return;
      }

      const payload = {
        idChat: this.chat.idChat,
        task: 'TAKE',
        anonimo: this.chat.typeChat === 'Anonymous',
      };
      this.takeChat(payload)
        .then(() => this.abrirChat(read))
        .catch(() => { if (!this.destroyed) this.genericoService.errorDialog('No se pudo tomar el chat. Puede estar asignado a otro agente.'); });
    } else {
      this.abrirChat(read);
    }
  }

  /**
   * Envía el chat al servicio para mostrarlo en la vista.
   * @param read - indica si es solo lectura
   */
  private abrirChat(read: boolean): void {
    if (this.destroyed) return;

    this.chatService.newChatView({
      ...this.chat,
      readMessages: read,
    });
  }


  /**
   * Llama al servicio para tomar el chat como agente.
   * @param payload - información requerida para tomar el chat
   * @returns Promise que se resuelve cuando se toma el chat
   */
  async takeChat(payload: any): Promise<void> {
    if (this.destroyed) throw new Error('Vista cerrada');
    await firstValueFrom(this.chatService.takeChat(payload).pipe(first(), takeUntil(this.destroyed$)));
  }

  /**
   * Genera el texto del diálogo de confirmación según el tipo de chat.
   * @returns Texto personalizado de confirmación.
   */
  private generarTextoConfirmacion(): string {
    return this.chat.typeChat === 'Anonymous'
      ? `¿Seguro que desea tomar el chat anónimo de: <b>${this.chat.usuario.nombre} - ${this.chat.usuario.telefono}<b>`
      : `¿Seguro que desea tomar el chat del negocio: <b>${this.chat.negocio.id} - ${this.chat.negocio.nombre}<b>?`;
  }


  /**
   * Acción para cerrar el chat si el usuario actual tiene permisos.
   * Muestra un diálogo de confirmación.
   */
  async closeChat() {
    if (!this.chat.agente || this.chat.agente.id !== getUserId()) {
      // this.mostrarDialogoPermiso();
      return;
    }

    const isConfirmed = await this.genericoService.confirmDialog(
      `Estás a punto de cerrar el chat. <br> Por favor, confirma si deseas finalizar la conversación.`
    );
    if (!isConfirmed || this.destroyed) {
      return;
    }

    const payload = {
      anonimo: this.chat.typeChat === 'Anonymous',
      idChat: this.chat.idChat,
      alertaClose: true,
    };
    this.chatService.closeChat(payload).pipe(first()).subscribe({next: () => this.chatService.closeChatView(this.chat.idChat.toString()), error: () => this.genericoService.errorDialog('No se pudo cerrar el chat. La conversación sigue abierta.')});
  }
}
